/**
 * One index_status poll loop for the whole app (VDR, Dataroom 2.0, Intelligence).
 * Prevents duplicate intervals and enforces a minimum gap between API calls.
 */

export const VDR_INDEX_STATUS_POLL_INTERVAL_MS = 5000;

let mountCount = 0;
let intervalId = null;
let inFlight = false;
let lastPollCompletedAt = 0;
let activeRunner = null;

const canStartPoll = () =>
    !inFlight && Date.now() - lastPollCompletedAt >= VDR_INDEX_STATUS_POLL_INTERVAL_MS;

const tick = () => {
    if (!activeRunner || !canStartPoll()) return;
    inFlight = true;
    Promise.resolve()
        .then(() => activeRunner())
        .catch(() => {})
        .finally(() => {
            inFlight = false;
            lastPollCompletedAt = Date.now();
        });
};

const ensureInterval = () => {
    if (intervalId != null) return;
    intervalId = setInterval(tick, VDR_INDEX_STATUS_POLL_INTERVAL_MS);
};

const clearIntervalIfIdle = () => {
    if (mountCount > 0 || intervalId == null) return;
    clearInterval(intervalId);
    intervalId = null;
};

/**
 * Register a poll runner while a route hook is mounted.
 * @param {() => Promise<void>|void} runPoll
 * @returns {() => void} unregister
 */
export const registerVdrIndexStatusPoller = runPoll => {
    mountCount += 1;
    activeRunner = runPoll;
    ensureInterval();
    if (canStartPoll()) {
        tick();
    }
    return () => {
        mountCount = Math.max(0, mountCount - 1);
        if (mountCount === 0) {
            activeRunner = null;
        }
        clearIntervalIfIdle();
    };
};

/** @visibleForTesting */
export const resetVdrIndexStatusPollCoordinator = () => {
    if (intervalId != null) {
        clearInterval(intervalId);
        intervalId = null;
    }
    mountCount = 0;
    inFlight = false;
    lastPollCompletedAt = 0;
    activeRunner = null;
};
